/**
 * Turning a Vendor Return or a Goods Discard into gate pass lines.
 *
 * A gate pass raised against one of these records only the physical exit — the
 * source document already decided what is leaving and how much of it. So the
 * lines are COPIED, not re-picked: the challan at the barrier and the document
 * in the system have to name the same items in the same quantities, and the one
 * reliable way to guarantee that is to never let them be typed twice.
 *
 * Import-free on purpose, so it can be unit tested — anything that reaches
 * api/inventory-request pulls in axios's ESM build and cannot load under Jest.
 * The fetching lives in GatePassForm; only the mapping lives here.
 */

/** A source line is always a store line: it names a real item and a real batch. */
const STORE_LINE = 'STORE';

/**
 * Only CONFIRMED vendor returns and APPROVED discards can back a gate pass.
 *
 * A draft is still editable, so its quantities can change after the challan is
 * printed; a cancelled or rejected one describes material that never left.
 */
export const GATE_PASS_SOURCE_READY_STATUS = {
  VENDOR_RETURN: 'CONFIRMED',
  GOODS_DISCARD: 'APPROVED',
};

export function isSourceDocumentReady(sourceType, row) {
  const wanted = GATE_PASS_SOURCE_READY_STATUS[sourceType];
  if (!wanted) return false;
  return String(row?.status || '').toUpperCase() === wanted;
}

/**
 * The store a source document belongs to.
 *
 * Vendor returns key it as `from_store_id` (grn-batch-service), discards as
 * `store_id` (stock-ledger-service). Neither list endpoint accepts a store
 * filter, so the narrowing happens here.
 */
export function sourceDocumentStoreId(row) {
  const id = row?.from_store_id ?? row?.store_id ?? '';
  return id == null ? '' : String(id);
}

export function sourceDocumentNumber(row) {
  return row?.return_number || row?.discard_number || row?.id || '';
}

export function sourceDocumentId(row) {
  return row?.id ?? row?.vendor_return_id ?? row?.discard_id ?? '';
}

/**
 * Select options for the source-document dropdown, narrowed to one store.
 *
 * An empty `storeId` yields nothing rather than everything: picking a document
 * from another store would fill the table with stock this pass cannot be
 * carrying, which is worse than an empty dropdown that says why.
 */
export function sourceDocumentOptions(sourceType, rows, storeId) {
  if (!storeId) return [];
  const store = String(storeId);
  return (Array.isArray(rows) ? rows : [])
    .filter(row => isSourceDocumentReady(sourceType, row))
    .filter(row => sourceDocumentStoreId(row) === store)
    .map(row => ({
      value: String(sourceDocumentId(row)),
      label: sourceDocumentNumber(row),
    }))
    .filter(opt => opt.value && opt.label);
}

function toLine(fields) {
  return {
    line_type: STORE_LINE,
    item_key: fields.item_id || '',
    item_id: fields.item_id || '',
    item_name: fields.item_name || '',
    item_description: '',
    grn_item_id: fields.grn_item_id || '',
    batch_number: fields.batch_number || '',
    expiry_date: fields.expiry_date || '',
    uom_id: fields.uom_id || '',
    uom_code: '',
    serial_number: '',
    _row_key: '',
    _batches: [],
    quantity: fields.quantity == null ? '' : String(fields.quantity),
    remarks: fields.remarks || '',
  };
}

/**
 * Vendor Return → gate pass lines.
 *
 * VendorReturnLineResponse carries no item_name — grn-batch-service resolves
 * nothing from inventory-master on this read — so the caller passes a lookup
 * built from the item catalog. `describe(item_id)` returns { item_name }.
 * The classification chip is resolved separately, at render, from item_id.
 */
export function vendorReturnToLines(doc, describe = () => ({})) {
  return (doc?.lines || [])
    .filter(l => l?.is_active !== false)
    .map((l) => {
      const described = describe(l.item_id) || {};
      return toLine({
        item_id: l.item_id,
        item_name: described.item_name,
        grn_item_id: l.grn_item_id,
        batch_number: l.batch_number,
        expiry_date: l.expiry_date,
        uom_id: l.uom_id,
        quantity: l.return_quantity,
        remarks: l.reason,
      });
    });
}

/**
 * Goods Discard → gate pass lines.
 *
 * GoodsDiscardLineResponse does resolve item_name on the get-by-id read, but it
 * is left None when the master lookup fails, so the same describe() fallback
 * applies. current_quantity is the on-hand at discard time and is deliberately
 * ignored — what leaves the gate is discard_quantity.
 */
export function goodsDiscardToLines(doc, describe = () => ({})) {
  return (doc?.lines || []).map((l) => {
    const described = describe(l.item_id) || {};
    return toLine({
      item_id: l.item_id,
      item_name: l.item_name || described.item_name,
      grn_item_id: l.grn_item_id,
      batch_number: l.batch_number,
      expiry_date: l.expiry_date,
      uom_id: l.uom_id,
      quantity: l.discard_quantity,
      remarks: l.remarks || l.reason,
    });
  });
}

export function sourceDocumentToLines(sourceType, doc, describe) {
  if (sourceType === 'VENDOR_RETURN') return vendorReturnToLines(doc, describe);
  if (sourceType === 'GOODS_DISCARD') return goodsDiscardToLines(doc, describe);
  return [];
}
